"use client";
import { useCallback, useContext } from "react";
import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { ErrorContext, IApiError } from "@/components/context";
import { getJWTInteractive, isInteractionRequired } from "@/lib/auth";
import { notifyError } from "@/lib/bugsnag";

export const SESSION_EXPIRED_MESSAGE =
    "Your session expired while this tab was open. Sign in again to continue — anything you typed has been saved.";

/**
 * Builds the expired-session banner, including the only affordance that gets the
 * user back in. Exported because `Providers` renders `ErrorContext` and so cannot
 * consume the hook below — it would read the default, no-op context value.
 */
export const buildSessionExpiredError = (instance: IPublicClientApplication, accounts: AccountInfo[]): IApiError => ({
    type: "session",
    message: SESSION_EXPIRED_MESSAGE,
    timestamp: Date.now(),
    action: {
        label: "Sign in again",
        onAction: () => {
            void getJWTInteractive(instance, accounts).catch((redirectError: unknown) => {
                // On success this has already navigated away, so reaching here
                // means the redirect could not even start.
                notifyError(redirectError instanceof Error ? redirectError : new Error(String(redirectError)), {
                    feature: "sessionExpiry",
                    operation: "interactiveSignIn",
                });
            });
        },
    },
});

export interface UseSessionExpiryReturn {
    reportIfSessionExpired: (error: unknown) => boolean;
    reportRequestFailure: (error: unknown, summary: string) => void;
}

/**
 * Turns a failed token acquisition into a visible, actionable banner.
 *
 * `getJWT` no longer navigates on its own, so something has to tell the user why
 * the app stopped working — `useIsAuthenticated()` reports account presence, not
 * token validity, so without this the UI keeps looking signed in.
 */
export const useSessionExpiry = (): UseSessionExpiryReturn => {
    const { instance, accounts } = useMsal();
    const { addError } = useContext(ErrorContext);

    const reportIfSessionExpired = useCallback(
        (error: unknown): boolean => {
            // Only a signed-in account can have an expired session. With no
            // account at all, LoggedIn's own redirect to /signin is the correct
            // outcome, and a banner here would just sit over the spinner.
            if (!isInteractionRequired(error) || accounts.length === 0) return false;

            addError(buildSessionExpiredError(instance, accounts));
            return true;
        },
        [accounts, addError, instance]
    );

    /**
     * Surfaces any failed request: as the session banner when the token can no
     * longer be renewed, otherwise as a plain API error. Data-loading effects
     * need this because `getJWT` no longer navigates away on expiry, so an
     * unhandled rejection would just leave a spinner up forever.
     */
    const reportRequestFailure = useCallback(
        (error: unknown, summary: string) => {
            if (reportIfSessionExpired(error)) return;

            const message = error instanceof Error ? error.message : String(error);
            addError({
                type: "api",
                message: `${summary}: ${message}`,
                details: error instanceof Error ? error.stack || message : message,
                timestamp: Date.now(),
            });
        },
        [addError, reportIfSessionExpired]
    );

    return { reportIfSessionExpired, reportRequestFailure };
};
